/**
 * PR73: Generated Redaction Policy Registry — Registry
 *
 * Metadata-only. No network call, no DB write, no secret literal,
 * no provider call, no file mutation at runtime, no command execution.
 * All pattern examples use safe placeholders — no real credentials.
 *
 * v2: Deep-frozen — every nested object and array is recursively frozen
 * so that runtime mutation of any pattern, policy, appliesTo, or
 * requiredPatternIds is impossible.
 */
// ---------------------------------------------------------------------------
// Deep freeze helper
// Recursively freezes every object and array in the tree.
// No network call, no DB write, no side effect — pure structural freeze.
// ---------------------------------------------------------------------------
function deepFreeze(value) {
    if (value === null || typeof value !== "object")
        return value;
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        const child = value[key];
        if (child !== null && typeof child === "object" && !Object.isFrozen(child)) {
            deepFreeze(child);
        }
    }
    return value;
}
// ---------------------------------------------------------------------------
// Redaction Patterns (RP001–RP020)
// ---------------------------------------------------------------------------
export const SMART_SHIKKA_REDACTION_PATTERNS = deepFreeze([
    {
        id: "RP001",
        name: "Secret Key (sk- prefix)",
        category: "secret_key",
        patternDescription: "Starts with sk- followed by alphanumeric characters",
        exampleMatch: "sk-" + "PLACEHOLDER_NOT_REAL",
        placeholder: "[REDACTED_SECRET_KEY]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace", "pr_report", "check_script_output"],
        required: true,
    },
    {
        id: "RP002",
        name: "GitHub Personal Access Token",
        category: "api_token",
        patternDescription: "Starts with ghp_ followed by alphanumeric characters",
        exampleMatch: "ghp_" + "PLACEHOLDER_NOT_REAL",
        placeholder: "[REDACTED_GITHUB_TOKEN]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace", "pr_report"],
        required: true,
    },
    {
        id: "RP003",
        name: "Supabase Project URL",
        category: "supabase_url",
        patternDescription: "Contains .supabase.co in URL",
        exampleMatch: "https://fake-project." + "supabase" + ".co",
        placeholder: "[REDACTED_SUPABASE_URL]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace", "connector_payload", "webhook_payload"],
        required: true,
    },
    {
        id: "RP004",
        name: "Database Connection URL",
        category: "database_url",
        patternDescription: "Starts with postgresql:// or mysql:// or mongodb:// with credentials",
        exampleMatch: "postgresql://user:pass@host/db",
        placeholder: "[REDACTED_DB_URL]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace"],
        required: true,
    },
    {
        id: "RP005",
        name: "JWT Token",
        category: "jwt_token",
        patternDescription: "Three base64url segments separated by dots (header.payload.signature)",
        exampleMatch: "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ0ZXN0In0.PLACEHOLDER",
        placeholder: "[REDACTED_JWT]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace", "connector_payload", "webhook_payload"],
        required: true,
    },
    {
        id: "RP006",
        name: "Razorpay Live Key",
        category: "razorpay_key",
        patternDescription: "Starts with rzp_live_ followed by alphanumeric characters",
        exampleMatch: "rzp_live_" + "PLACEHOLDER_NOT_REAL",
        placeholder: "[REDACTED_RAZORPAY_KEY]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace", "connector_payload"],
        required: true,
    },
    {
        id: "RP007",
        name: "Shiprocket Token",
        category: "shiprocket_token",
        patternDescription: "Shiprocket API token pattern",
        exampleMatch: "shiprocket_token_PLACEHOLDER",
        placeholder: "[REDACTED_SHIPROCKET_TOKEN]",
        action: "replace_with_placeholder",
        severity: "high",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "connector_payload"],
        required: true,
    },
    {
        id: "RP008",
        name: "Email Address",
        category: "email_address",
        patternDescription: "Standard email format user@domain.tld",
        exampleMatch: "user@example.com",
        placeholder: "[REDACTED_EMAIL]",
        action: "replace_with_placeholder",
        severity: "medium",
        appliesTo: ["audit_log", "report_output", "agent_trace", "user_facing_message"],
        required: false,
    },
    {
        id: "RP009",
        name: "Phone Number",
        category: "phone_number",
        patternDescription: "10-digit or international format phone number",
        exampleMatch: "+91-9999999999",
        placeholder: "[REDACTED_PHONE]",
        action: "replace_with_placeholder",
        severity: "medium",
        appliesTo: ["audit_log", "report_output", "user_facing_message"],
        required: false,
    },
    {
        id: "RP010",
        name: "Internal IP Address",
        category: "ip_address",
        patternDescription: "Private IP ranges: 10.x.x.x, 172.16-31.x.x, 192.168.x.x",
        exampleMatch: "192.168.1.100",
        placeholder: "[REDACTED_INTERNAL_IP]",
        action: "replace_with_placeholder",
        severity: "medium",
        appliesTo: ["audit_log", "error_message", "report_output", "agent_trace", "healthcheck_result"],
        required: false,
    },
    {
        id: "RP011",
        name: "Internal Hostname",
        category: "internal_hostname",
        patternDescription: "Internal hostnames ending in .internal, .local, .corp",
        exampleMatch: "db-server.internal",
        placeholder: "[REDACTED_INTERNAL_HOST]",
        action: "replace_with_placeholder",
        severity: "medium",
        appliesTo: ["audit_log", "error_message", "report_output", "healthcheck_result"],
        required: false,
    },
    {
        id: "RP012",
        name: "AI Provider Endpoint",
        category: "provider_endpoint",
        patternDescription: "AI provider API endpoints containing api.openai.com or similar",
        exampleMatch: "https://api.openai.com/v1/completions",
        placeholder: "[REDACTED_PROVIDER_ENDPOINT]",
        action: "replace_with_placeholder",
        severity: "high",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace", "provider_response"],
        required: true,
    },
    {
        id: "RP013",
        name: "AI Model API Key",
        category: "ai_model_key",
        patternDescription: "OpenAI/Anthropic/Gemini API key patterns",
        exampleMatch: "sk-ant-" + "PLACEHOLDER_NOT_REAL",
        placeholder: "[REDACTED_AI_KEY]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace"],
        required: true,
    },
    {
        id: "RP014",
        name: "Sentry DSN",
        category: "sentry_dsn",
        patternDescription: "Sentry DSN format https://key@sentry.io/project",
        exampleMatch: "https://PLACEHOLDER@sentry.io/12345",
        placeholder: "[REDACTED_SENTRY_DSN]",
        action: "replace_with_placeholder",
        severity: "high",
        appliesTo: ["audit_log", "error_message", "report_output", "agent_trace"],
        required: true,
    },
    {
        id: "RP015",
        name: "Webhook Secret",
        category: "webhook_secret",
        patternDescription: "Webhook HMAC secret or signing key",
        exampleMatch: "whsec_" + "PLACEHOLDER_NOT_REAL",
        placeholder: "[REDACTED_WEBHOOK_SECRET]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "connector_payload", "webhook_payload"],
        required: true,
    },
    {
        id: "RP016",
        name: "Private Key PEM",
        category: "private_key_pem",
        patternDescription: "PEM-encoded private key block",
        exampleMatch: "-----BEGIN PRIVATE KEY-----",
        placeholder: "[REDACTED_PRIVATE_KEY]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "report_output", "agent_trace"],
        required: true,
    },
    {
        id: "RP017",
        name: "Bearer Token Header",
        category: "bearer_token",
        patternDescription: "Authorization: Bearer <token> header value",
        exampleMatch: "Bearer PLACEHOLDER_TOKEN_NOT_REAL",
        placeholder: "[REDACTED_BEARER_TOKEN]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "connector_payload", "webhook_payload"],
        required: true,
    },
    {
        id: "RP018",
        name: "Basic Auth Header",
        category: "basic_auth_header",
        patternDescription: "Authorization: Basic <base64> header value",
        exampleMatch: "Basic dXNlcjpwYXNz",
        placeholder: "[REDACTED_BASIC_AUTH]",
        action: "replace_with_placeholder",
        severity: "critical",
        appliesTo: ["audit_log", "api_response", "error_message", "connector_payload"],
        required: true,
    },
    {
        id: "RP019",
        name: "S3 Bucket URL with credentials",
        category: "s3_bucket_url",
        patternDescription: "S3 presigned URL with X-Amz-Signature parameter",
        exampleMatch: "https://bucket.s3.amazonaws.com/key?X-Amz-Signature=PLACEHOLDER",
        placeholder: "[REDACTED_S3_URL]",
        action: "replace_with_placeholder",
        severity: "high",
        appliesTo: ["audit_log", "api_response", "report_output", "connector_payload"],
        required: false,
    },
    {
        id: "RP020",
        name: "CDN URL with token",
        category: "cdn_url",
        patternDescription: "CDN URL containing token= or auth= query parameter",
        exampleMatch: "https://cdn.example.com/file?token=PLACEHOLDER",
        placeholder: "[REDACTED_CDN_URL]",
        action: "replace_with_placeholder",
        severity: "medium",
        appliesTo: ["audit_log", "api_response", "report_output"],
        required: false,
    },
]);
// ---------------------------------------------------------------------------
// Redaction Policies (POL001–POL014)
// ---------------------------------------------------------------------------
export const SMART_SHIKKA_REDACTION_POLICIES = deepFreeze([
    {
        id: "POL001",
        name: "Audit Log Redaction Policy",
        scope: "audit_log",
        requiredPatternIds: ["RP001", "RP002", "RP003", "RP004", "RP005", "RP006", "RP007", "RP012", "RP013", "RP014", "RP015", "RP016", "RP017", "RP018"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL002",
        name: "API Response Redaction Policy",
        scope: "api_response",
        requiredPatternIds: ["RP001", "RP002", "RP003", "RP005", "RP006", "RP012", "RP013", "RP015", "RP016", "RP017", "RP018"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL003",
        name: "Error Message Redaction Policy",
        scope: "error_message",
        requiredPatternIds: ["RP001", "RP002", "RP003", "RP004", "RP005", "RP006", "RP012", "RP013", "RP014", "RP016", "RP017"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL004",
        name: "Test Fixture Redaction Policy",
        scope: "test_fixture",
        requiredPatternIds: ["RP001", "RP002", "RP003", "RP005", "RP006", "RP013", "RP015"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL005",
        name: "Report Output Redaction Policy",
        scope: "report_output",
        requiredPatternIds: ["RP001", "RP002", "RP003", "RP004", "RP005", "RP006", "RP007", "RP012", "RP013", "RP014", "RP016"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL006",
        name: "Agent Trace Redaction Policy",
        scope: "agent_trace",
        requiredPatternIds: ["RP001", "RP002", "RP003", "RP004", "RP005", "RP006", "RP012", "RP013", "RP014", "RP016", "RP017"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL007",
        name: "PR Report Redaction Policy",
        scope: "pr_report",
        requiredPatternIds: ["RP001", "RP002", "RP003", "RP005", "RP006", "RP013"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL008",
        name: "Check Script Output Redaction Policy",
        scope: "check_script_output",
        requiredPatternIds: ["RP001", "RP002", "RP013"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL009",
        name: "Manifest Export Redaction Policy",
        scope: "manifest_export",
        requiredPatternIds: [],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL010",
        name: "Connector Payload Redaction Policy",
        scope: "connector_payload",
        requiredPatternIds: ["RP003", "RP005", "RP006", "RP007", "RP015", "RP017", "RP018"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL011",
        name: "Webhook Payload Redaction Policy",
        scope: "webhook_payload",
        requiredPatternIds: ["RP003", "RP005", "RP015", "RP017"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL012",
        name: "Healthcheck Result Redaction Policy",
        scope: "healthcheck_result",
        requiredPatternIds: [],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL013",
        name: "Provider Response Redaction Policy",
        scope: "provider_response",
        requiredPatternIds: ["RP012"],
        active: true,
        introducedInPr: 73,
    },
    {
        id: "POL014",
        name: "User Facing Message Redaction Policy",
        scope: "user_facing_message",
        requiredPatternIds: [],
        active: true,
        introducedInPr: 73,
    },
]);
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const SMART_SHIKKA_REDACTION_POLICY_REGISTRY = deepFreeze({
    patterns: SMART_SHIKKA_REDACTION_PATTERNS,
    policies: SMART_SHIKKA_REDACTION_POLICIES,
    version: "1.0.0",
    generatedInPr: 73,
});
// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------
export function getRedactionPatternById(id) {
    return SMART_SHIKKA_REDACTION_PATTERNS.find((p) => p.id === id);
}
export function getRedactionPolicyByScope(scope) {
    return SMART_SHIKKA_REDACTION_POLICIES.find((p) => p.scope === scope);
}
export function getRequiredPatterns() {
    return SMART_SHIKKA_REDACTION_PATTERNS.filter((p) => p.required);
}
export function getPatternsForScope(scope) {
    return SMART_SHIKKA_REDACTION_PATTERNS.filter((p) => p.appliesTo.includes(scope));
}
export function getActivePolicies() {
    return SMART_SHIKKA_REDACTION_POLICIES.filter((p) => p.active);
}
//# sourceMappingURL=redactionPolicyRegistry.js.map